import { EmptyResponseEntity } from "types/emptyResponseEntity";
import { type AccountingIntegrationType } from "types/enum";

import { APIHandler, type IBlobResponse } from "utilities/apiHandler";

import {
    AccountingSettingsData,
    AccountingSubmitInitialTaxOptionsResponse,
    AccountingUrlResponse,
    type IAccountingSettingsFormValues,
    type IAccountingSubmitInitialTaxOptionsRequest,
    type IFinalizeConnectionToAccountingRequest,
    OAuthData,
    QbdEntityThresholdResponse,
    QBSyncResponse,
    SyncAccountingMethodResponse,
} from "./AccountingSettings.api.types";

export interface IEstablishAccountingConnectionHandler {
    establishConnectionToAccounting(
        integrationType: AccountingIntegrationType,
    ): Promise<AccountingUrlResponse>;
    finalizeConnectionToAccounting(
        requestData: IFinalizeConnectionToAccountingRequest,
    ): Promise<EmptyResponseEntity>;
    getToken(integrationType: AccountingIntegrationType, code: string): Promise<OAuthData>;
}

export class EstablishAccountingConnectionHandler implements IEstablishAccountingConnectionHandler {
    async establishConnectionToAccounting(
        integrationType: AccountingIntegrationType,
    ): Promise<AccountingUrlResponse> {
        return await APIHandler(
            `/api/Accounting/EstablishConnectionToAccounting?integrationType=${integrationType}`,
            { method: "PUT", responseType: AccountingUrlResponse },
        );
    }

    async finalizeConnectionToAccounting(
        requestData: IFinalizeConnectionToAccountingRequest,
    ): Promise<EmptyResponseEntity> {
        return await APIHandler("/api/Accounting/FinalizeConnectionToAccounting", {
            method: "PUT",
            data: requestData,
            responseType: EmptyResponseEntity,
        });
    }

    async getToken(integrationType: AccountingIntegrationType, code: string): Promise<OAuthData> {
        return await APIHandler(
            `/api/Accounting/Token?integrationType=${integrationType}&code=${code}`,
            { method: "GET", responseType: OAuthData },
        );
    }
}

export interface IAccountingSettingsHandler extends IEstablishAccountingConnectionHandler {
    get(): Promise<AccountingSettingsData>;
    disconnectSuspendedAccount(): Promise<EmptyResponseEntity>;
    clearAccountingCache(idTypes: number[]): Promise<EmptyResponseEntity>;
    disconnect(): Promise<EmptyResponseEntity>;
    submitInitialTaxOption(
        formData: IAccountingSubmitInitialTaxOptionsRequest,
    ): Promise<AccountingSubmitInitialTaxOptionsResponse>;
    clearPendingSyncTransactions(): Promise<EmptyResponseEntity>;
    saveSettings(formData: IAccountingSettingsFormValues): Promise<EmptyResponseEntity>;
    builderApprovedUpgrade(): Promise<EmptyResponseEntity>;
    getWebConnectorConfig(): Promise<IBlobResponse>;
    getQBSyncData(): Promise<QBSyncResponse>;
    manualSyncRequest(): Promise<QBSyncResponse>;
    qbdEntityThreshold(idTypes: number[]): Promise<QbdEntityThresholdResponse>;
    syncAccountingMethod(): Promise<SyncAccountingMethodResponse>;
    getAccountingOAuthUrl(
        integrationType: AccountingIntegrationType,
    ): Promise<AccountingUrlResponse>;
    exchangeAndUpdateToken(
        integrationType: AccountingIntegrationType,
        code: string,
    ): Promise<EmptyResponseEntity>;
}

export class AccountingSettingsHandler
    extends EstablishAccountingConnectionHandler
    implements IAccountingSettingsHandler
{
    async get(): Promise<AccountingSettingsData> {
        return await APIHandler(`/api/Accounting/GetAccountingSetupLoad`, {
            method: "GET",
            responseType: AccountingSettingsData,
        });
    }

    async disconnectSuspendedAccount(): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Accounting/DisconnectSuspendedAccount`, {
            method: "POST",
            responseType: EmptyResponseEntity,
        });
    }

    async clearAccountingCache(idTypes: number[]): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Accounting/ClearCache`, {
            method: "POST",
            data: { idTypes },
            responseType: EmptyResponseEntity,
        });
    }

    async disconnect(): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Accounting/Disconnect`, {
            method: "POST",
            responseType: EmptyResponseEntity,
        });
    }

    async submitInitialTaxOption(
        formData: IAccountingSubmitInitialTaxOptionsRequest,
    ): Promise<AccountingSubmitInitialTaxOptionsResponse> {
        return await APIHandler(`/api/Accounting/SubmitInitialTaxOption`, {
            method: "POST",
            data: formData,
            responseType: AccountingSubmitInitialTaxOptionsResponse,
        });
    }

    async clearPendingSyncTransactions(): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Accounting/ClearPendingSyncTransactions`, {
            method: "POST",
            responseType: EmptyResponseEntity,
        });
    }

    async saveSettings(formData: IAccountingSettingsFormValues): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Accounting/Setup`, {
            method: "PUT",
            data: formData,
            responseType: EmptyResponseEntity,
        });
    }

    async builderApprovedUpgrade(): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Accounting/BuilderApprovedUpgrade`, {
            method: "PUT",
            responseType: EmptyResponseEntity,
        });
    }

    async getWebConnectorConfig(): Promise<IBlobResponse> {
        return await APIHandler("/api/Accounting/GetWebConnectorConfig", {
            method: "GET",
            responseType: "blob",
        });
    }

    async getQBSyncData(): Promise<QBSyncResponse> {
        return await APIHandler("/api/Accounting/GetSyncSchedule", {
            method: "GET",
            responseType: QBSyncResponse,
        });
    }

    async manualSyncRequest(): Promise<QBSyncResponse> {
        return await APIHandler("/api/Accounting/ManualSyncRequest", {
            method: "GET",
            responseType: QBSyncResponse,
        });
    }

    async qbdEntityThreshold(idTypes: number[]): Promise<QbdEntityThresholdResponse> {
        return await APIHandler(`/api/Accounting/QBDEntityThreshold`, {
            method: "GET",
            data: { idTypes },
            responseType: QbdEntityThresholdResponse,
        });
    }

    async syncAccountingMethod(): Promise<SyncAccountingMethodResponse> {
        return await APIHandler("/api/Accounting/SyncAccountingMethod", {
            method: "PUT",
            responseType: SyncAccountingMethodResponse,
        });
    }

    async getAccountingOAuthUrl(
        integrationType: AccountingIntegrationType,
    ): Promise<AccountingUrlResponse> {
        return await APIHandler(
            `/api/Accounting/GetAccountingOAuthUrl?integrationType=${integrationType}`,
            { method: "PUT", responseType: AccountingUrlResponse },
        );
    }

    async exchangeAndUpdateToken(
        integrationType: AccountingIntegrationType,
        code: string,
    ): Promise<EmptyResponseEntity> {
        return await APIHandler(
            `/api/Accounting/ExchangeAndUpdateXeroToken?integrationType=${integrationType}&code=${code}`,
            { method: "GET", responseType: OAuthData },
        );
    }
}
